import { StyledButton, lightColors } from '@tryhackme/thm-ui-components';
import { type FC, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ThemeProvider, useTheme } from 'styled-components';
import { useGetUserQuery } from 'src/common/slices';
import { useCampaignCta } from 'src/features/campaigns/hooks/use-campaign-cta';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CampaignName } from 'src/common/enums/campaigns';
import { useInfluencerSubscriptionAdditionalConfigs } from '../../hooks/use-influencer-additional-configs';
import { useInfluencerSubscriptionCampaign } from '../../hooks/use-influencer-campaign';
import { useShowInfluencerCampaignBar } from '../influencer-campaign-bar/hooks/use-show-influencer-campaign-bar';
import { InfluencerIcon } from '../influencer-icon';
import { StyledBar, StyledCampaignPromotionText, StyledCloseBtn, StyledCopyAndIconWrapper } from './influencer-subscription-campaign-bar.styles';
import type { InfluencerSubscriptionCampaignBarProps } from './influencer-subscription-campaign-bar.types';
export const InfluencerSubscriptionCampaignBar: FC<InfluencerSubscriptionCampaignBarProps> = ({
  influencerCampaign,
  isEmbedded,
  screenEmbedded
}) => {
  const theme = useTheme();
  const {
    pathname
  } = useLocation();
  const [shouldShow, setShouldShow] = useState(false);
  const {
    data: userDataResponse
  } = useGetUserQuery();
  const [ctaText, loggedUserUrlToNavigate] = useCampaignCta();
  const {
    campaign,
    isInCurrentInfluencerSession,
    userFriendlyName,
    percentageOff
  } = useInfluencerSubscriptionCampaign(influencerCampaign);
  const [isShowInfluencerCampaignBar, hideInfluencerCampaignBar] = useShowInfluencerCampaignBar();
  const additionalConfigs = useInfluencerSubscriptionAdditionalConfigs(campaign?.name);
  const {
    pagesToShowCTA,
    barCta,
    icon,
    urlToNavigate
  } = additionalConfigs ?? {};
  const url = userDataResponse?.data ? loggedUserUrlToNavigate : urlToNavigate;
  useEffect(() => {
    const show = !!isInCurrentInfluencerSession && !!campaign;
    setShouldShow(show);
  }, [isInCurrentInfluencerSession, campaign, pathname, userDataResponse?.data, pagesToShowCTA]);
  if (!campaign || !isShowInfluencerCampaignBar) return null;
  if (!Object.values(CampaignName).includes(campaign.name)) {
    return null;
  }
  const barCtaContent = barCta ? barCta(userFriendlyName, percentageOff, pathname === '/onboarding') : null;
  const isGradient = campaign.name === CampaignName.FIRESHIP_2025;
  const canTakeActions = !['/why-subscribe', '/pricing', '/onboarding'].includes(pathname);
  return shouldShow ? <ThemeProvider theme={{
    ...theme,
    colors: lightColors
  }}>
      <StyledBar isGradient={isGradient} isEmbedded={isEmbedded} screenEmbedded={screenEmbedded} role="region" aria-label="Influencer Special Offer">
        {icon ? <StyledCopyAndIconWrapper>
            {icon && userFriendlyName && <InfluencerIcon icon={icon} alt={userFriendlyName} />}
            <p data-testid="campaign-promotion-text">{barCtaContent}</p>
          </StyledCopyAndIconWrapper> : <StyledCampaignPromotionText data-testid="campaign-promotion-text">
            <span>Limited time offer:&nbsp;</span>
            {barCtaContent}
          </StyledCampaignPromotionText>}
        {canTakeActions && <>
            <StyledButton as={Link} to={url ?? ''} variant="solid" color="primary" size="small">
              {ctaText}
            </StyledButton>
            {!screenEmbedded && <StyledCloseBtn onClick={hideInfluencerCampaignBar}>
                <FontAwesomeIcon icon={faXmark} />
              </StyledCloseBtn>}
          </>}
      </StyledBar>
    </ThemeProvider> : null;
};